// src/Components/LocationList.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import '../css/LocationMap.css';

function BuildingIso({ name, onOpen, idx, active, onSelect }) {
  // 이름 기반 시드 → 창문 패턴 고정
  const seed = React.useMemo(() => {
    let s = 0; const str = String(name || '');
    for (let i = 0; i < str.length; i++) s = (s * 131 + str.charCodeAt(i)) >>> 0;
    return s + idx;
  }, [name, idx]);

  // 높이/폭 비율 변형 (타워/미드/로우)
  const variant = React.useMemo(() => {
    const r = seed % 100;
    if (r < 33) return 'tower';
    if (r < 66) return 'mid';
    return 'low';
  }, [seed]);

  // 클릭/키보드: 선택 + 첫 층 이동
  const onClick = useCallback(() => {
    onSelect(idx);
    onOpen(name);
  }, [idx, name, onSelect, onOpen]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(idx);
      onOpen(name);
    }
  }, [idx, name, onOpen, onSelect]);

  return (
    <div
      className={`iso-bldg ${variant} ${active ? 'active' : ''}`}
      role="option"
      aria-selected={active}
      tabIndex={0}
      title={name}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {/* 2.5D 상단/측면 */}
      <div className="iso-top" aria-hidden="true" />
      <div className="iso-side" aria-hidden="true" />

      {/* 전면(창문/문/이름) */}
      <div className="iso-front">
        <div className="iso-win-grid">
          {Array.from({ length: 40 }).map((_, i) => {
            const on = ((seed >> (i % 31)) & 1) === 1;
            return <div key={i} className={`iso-win ${on ? 'on' : 'off'}`} />;
          })}
        </div>
        <div className="iso-foot">
          <div className="iso-door" />
          <div className="iso-name" aria-label={name}>{name}</div>
        </div>
      </div>

      {/* 네온 하이라이트 */}
      <div className="iso-glow" aria-hidden="true" />
    </div>
  );
}

export default function LocationList() {
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();

  const railRef = useRef(null);

  // 드래그 스크롤(선택은 변경 안 함)
  const dragging = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);

  // Press&Hold (버튼)
  const HOLD_START_DELAY = 220; // ms: 이 시간 지나면 연속 이동
  const REPEAT_MIN_DELAY = 90;  // ms: 반복 최소 간격
  const REPEAT_ACCEL = 15;      // ms: 반복 가속

  const pressStartAtRef = useRef(0);
  const repeatTimerRef = useRef(null);
  const repeatDelayRef = useRef(HOLD_START_DELAY);
  const repeatDirRef = useRef(0);  // -1 / +1
  const didRepeatRef = useRef(false);
  const activeIdxRef = useRef(0);
  useEffect(() => { activeIdxRef.current = activeIdx; }, [activeIdx]);

  // 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/locations');
        setLocations(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('로케이션 로드 실패', e);
      }
    })();
  }, []);

  // 검색 필터
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return !t ? locations : locations.filter((l) => (l.name || '').toLowerCase().includes(t));
  }, [q, locations]);

  // 필터 바뀌면 인덱스 보정
  useEffect(() => {
    setActiveIdx((prev) => Math.min(prev, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  // 첫 층 이동
  const goToFirstFloor = useCallback(async (locName) => {
    try {
      const { data: loc } = await api.get('/location', { params: { name: locName } });
      const first = loc?.floors?.[0]?.name;
      if (!first) return alert('해당 위치에 등록된 층이 없습니다.');
      navigate(`/floorplan/${encodeURIComponent(locName)}/${encodeURIComponent(first)}`);
    } catch (e) {
      console.error('첫 층 로드 실패:', e);
      alert('층 정보를 불러오지 못했습니다.');
    }
  }, [navigate]);

  // 특정 인덱스로 중앙 스크롤
  const scrollToIndex = useCallback((idx, smooth = true) => {
    const rail = railRef.current;
    if (!rail) return;
    const cards = rail.querySelectorAll('.iso-bldg');
    const el = cards[idx];
    if (!el) return;
    el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // 초기 중앙 근처로
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    if (rail.scrollWidth > rail.clientWidth) {
      rail.scrollLeft = (rail.scrollWidth - rail.clientWidth) / 2;
    }
    scrollToIndex(activeIdx, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  // 선택(인덱스) 변경
  const selectIdx = useCallback((idx) => {
    const next = Math.max(0, Math.min(idx, filtered.length - 1));
    setActiveIdx(next);
    scrollToIndex(next, true);
  }, [filtered.length, scrollToIndex]);

  // 키보드 좌우/엔터
  const onRailKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); selectIdx(activeIdx + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); selectIdx(activeIdx - 1); }
    else if (e.key === 'Enter') {
      const name = filtered[activeIdx]?.name;
      if (name) goToFirstFloor(name);
    }
  }, [activeIdx, filtered, goToFirstFloor, selectIdx]);

  // 드래그 스크롤
  const onPointerDown = useCallback((e) => {
    const rail = railRef.current;
    if (!rail) return;
    dragging.current = true;
    rail.classList.add('dragging');
    startX.current = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    startScroll.current = rail.scrollLeft;
    e.preventDefault();
  }, []);
  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    const rail = railRef.current;
    if (!rail) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const dx = x - startX.current;
    rail.scrollLeft = startScroll.current - dx;
  }, []);
  const endDrag = useCallback(() => {
    const rail = railRef.current;
    dragging.current = false;
    rail?.classList.remove('dragging');
  }, []);
  const onWheel = useCallback((e) => {
    const rail = railRef.current;
    if (!rail) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      rail.scrollBy({ left: e.deltaY, behavior: 'auto' });
    }
  }, []);

  // ----- Press & Hold 구현 (클릭 vs 꾹 누름 분리) -----
  const stepOnce = useCallback((dir) => {
    setActiveIdx((cur) => {
      const next = Math.max(0, Math.min(cur + dir, filtered.length - 1));
      if (next !== cur) scrollToIndex(next, true);
      return next;
    });
  }, [filtered.length, scrollToIndex]);

  const repeatTick = useCallback(() => {
    didRepeatRef.current = true;
    stepOnce(repeatDirRef.current);
    repeatDelayRef.current = Math.max(REPEAT_MIN_DELAY, repeatDelayRef.current - REPEAT_ACCEL);
    repeatTimerRef.current = setTimeout(repeatTick, repeatDelayRef.current);
  }, [REPEAT_ACCEL, REPEAT_MIN_DELAY, stepOnce]);

  const onPressStart = useCallback((dir) => {
    repeatDirRef.current = dir;
    didRepeatRef.current = false;
    repeatDelayRef.current = HOLD_START_DELAY;
    pressStartAtRef.current = Date.now();
    // HOLD_START_DELAY 뒤에 연속 이동 시작
    repeatTimerRef.current = setTimeout(repeatTick, HOLD_START_DELAY);
  }, [HOLD_START_DELAY, repeatTick]);

  const onPressEnd = useCallback(() => {
    const pressedFor = Date.now() - pressStartAtRef.current;
    if (repeatTimerRef.current) {
      clearTimeout(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
    // 연속 이동이 시작되지 않았다면 = 짧게 눌렀다면 → 1칸만 이동
    if (!didRepeatRef.current && pressedFor < HOLD_START_DELAY + 20) {
      stepOnce(repeatDirRef.current);
    }
    repeatDirRef.current = 0;
    didRepeatRef.current = false;
  }, [HOLD_START_DELAY, stepOnce]);

  // 안전장치: 창 포커스 잃거나 포인터가 밖으로 나가면 종료
  useEffect(() => {
    const cancel = () => onPressEnd();
    window.addEventListener('pointerup', cancel);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('pointerup', cancel);
      window.removeEventListener('blur', cancel);
    };
  }, [onPressEnd]);

  return (
    <div className="location-page">
      <div className="header-bar">
        <div className="search-wrap">
          <span>🔍</span>
          <input
            className="search-input"
            placeholder="위치명 검색"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

   
      
      </div>

      <div
        className="iso-rail"
        ref={railRef}
        role="listbox"
        aria-label="빌딩 목록"
        tabIndex={0}
        onKeyDown={onRailKeyDown}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseLeave={endDrag}
        onMouseUp={endDrag}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={endDrag}
        onWheel={onWheel}
      >
        <div className="iso-row">
          {filtered.length === 0 ? (
            <div className="empty">등록된 위치가 없거나 검색 결과가 없습니다.</div>
          ) : (
            filtered.map((l, idx) => (
              <BuildingIso
                key={l.name}
                name={l.name}
                idx={idx}
                active={idx === activeIdx}
                onOpen={goToFirstFloor}
                onSelect={selectIdx}
              />
            ))
          )}
        </div>
      </div>
  <div className="rail-ctrl" aria-label="가로 목록 이동">
      <button
        className="rail-btn prev"
        aria-label="이전"
        onPointerDown={(e) => { e.preventDefault(); if (activeIdx > 0) onPressStart(-1); }}
        onPointerUp={onPressEnd}
        onPointerLeave={onPressEnd}
        disabled={activeIdx <= 0}
      >◀</button>

      <button
        className="rail-btn next"
        aria-label="다음"
        onPointerDown={(e) => { e.preventDefault(); if (activeIdx < filtered.length - 1) onPressStart(+1); }}
        onPointerUp={onPressEnd}
        onPointerLeave={onPressEnd}
        disabled={activeIdx >= filtered.length - 1}
      >▶</button>
    </div>
    </div>
  );
}
